import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Tagline} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {reload} from "@khan/urls";
import {
    AccessibilitySettings,
    LinkedAccounts,
    PasswordForm,
    VideoSettings,
} from "@khan/user-settings";

import {AccountSettingsQuery} from "./account-settings-query.tsx";
import BasicSettings from "./basic-settings.tsx";
import ChildrenSection from "./children-section.tsx";
import CoachesSection from "./coaches-section.tsx";
import DeleteAccount from "./delete-account.tsx";
import GradeLevelsAndSubjectsSection from "./grade-levels-and-subjects-section.tsx";
import ParentEmailSection from "./parent-email-section.tsx";
import PublicProfileInfoSection from "./public-profile-info-section.tsx";
import RolesSection from "./roles-section.tsx";
import SchoolSection from "./school-section.tsx";
import {SecuritySettings} from "./security-settings.tsx";
import TeacherAffiliationSection from "./teacher-affiliation-section.tsx";

const popSuccessToast = () =>
    import("../notifications-package/toast-notification.tsx").then(
        ({renderChangesSavedToast}) => {
            renderChangesSavedToast();
        },
    );

const handleSuccessfulChange = async () => {
    await popSuccessToast();
    return reload();
};

export type Props = {
    targetKaid: string;
};

const AccountSettings = (props: Props) => {
    const twoFactorAuthFlag = useFlagOrCookie("two_factor_authentication");

    // NOTE: No need to port the feature flag over to new classroom/polaris code
    // we can simply always render grade levels section
    const newGradeLevelsAndSubjectsSection = useFeatureIsOn<AppFeatures>(
        "show-grade-levels-and-subjects-section",
    );

    return (
        <AccountSettingsQuery targetKaid={props.targetKaid}>
            {({
                actor,
                target,
                requestInfo,
                sessionHash,
                isLoading,
                isError,
            }) => {
                if (isLoading) {
                    return (
                        <CircularSpinner size="medium" style={styles.spinner} />
                    );
                }

                if (
                    !actor ||
                    !target ||
                    !requestInfo ||
                    !sessionHash ||
                    isError
                ) {
                    return (
                        <Tagline style={styles.text}>
                            {t`Oops! Looks like we don’t have data available for this user.`}
                        </Tagline>
                    );
                }

                const {
                    hideVisual,
                    prefersReducedMotion,
                    noColorInVideos,
                    showCaptions,
                    playbackRate,
                    muteVideos,
                    soundOn,
                    ...basicSettings
                } = target.settings;

                return (
                    <View>
                        <BasicSettings
                            actorIdentifier={actor.identifier}
                            actorIsChild={actor.isChild}
                            kaid={target.id}
                            targetIsChild={target.isChild}
                            isModifyingChild={requestInfo.isModifyingChild}
                            initialSettings={basicSettings}
                            successfullyChangedSettings={handleSuccessfulChange}
                            userChosenRoles={target.userChosenRoles || []}
                            hasDistrictProvidedBirthdate={
                                target.hasDistrictProvidedBirthdate
                            }
                            includesDistrictOwnedData={
                                target.includesDistrictOwnedData
                            }
                            includesCentrallyOwnedDistrictData={
                                target.includesCentrallyOwnedDistrictData
                            }
                            isClasslinkUser={target.isClasslinkUser}
                        />
                        <View style={styles.divider} />
                        <VideoSettings
                            kaid={target.id}
                            initialSettings={{
                                muteVideos: muteVideos,
                                showCaptions,
                                playbackRate:
                                    playbackRate || "RATE_100_PERCENT",
                            }}
                            successfullyChangedSettings={handleSuccessfulChange}
                        />
                        <View style={styles.divider} />
                        {target.isChild && (
                            <React.Fragment>
                                <ParentEmailSection
                                    identifier={actor.identifier}
                                    isClasslinkUser={target.isClasslinkUser}
                                    isModifyingChild={
                                        requestInfo.isModifyingChild
                                    }
                                    managingParentEmail={
                                        target.managingParentEmail
                                    }
                                    underAgeGateParentEmail={
                                        target.underAgeGateParentEmail
                                    }
                                    canUpdateParentEmail={
                                        target.canUpdateParentEmail
                                    }
                                    daysUntilCutoff={target.daysUntilCutoff}
                                    pendingParentEmail={
                                        target.pendingParentEmail
                                    }
                                    profileRoot={target.profileRoot}
                                    successfullyChangedSettings={
                                        handleSuccessfulChange
                                    }
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {(requestInfo.isModifyingChild ||
                            target.hasSendableEmail) && (
                            <React.Fragment>
                                <PasswordForm
                                    actorIdentifier={actor.identifier}
                                    actorIsChild={actor.isChild}
                                    targetId={target.id}
                                    isClasslinkUser={target.isClasslinkUser}
                                    isModifyingChild={
                                        requestInfo.isModifyingChild
                                    }
                                    hasPassword={target.hasPassword}
                                    requiresExistingPassword={
                                        requestInfo.requiresExistingPassword
                                    }
                                    successfullyChangedSettings={
                                        handleSuccessfulChange
                                    }
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {/* target.isChild is needed here because "child"
                            in isModifyingChild refers to the parent/child
                            relationship, not the age of the target.
                            target.isChild specifies that the target is
                            an age-restricted account. */}
                        {requestInfo.isModifyingChild && target.isChild && (
                            <React.Fragment>
                                <CoachesSection
                                    targetId={target.id}
                                    canModifyCoaches={target.canModifyCoaches}
                                    profileRoot={target.profileRoot}
                                    successfullyChangedSettings={
                                        handleSuccessfulChange
                                    }
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {!target.isChild && (
                            <React.Fragment>
                                <LinkedAccounts
                                    age={target.age}
                                    authEmails={target.authEmails}
                                    hasPassword={target.hasPassword}
                                    isModifyingChild={
                                        requestInfo.isModifyingChild
                                    }
                                    identifier={actor.identifier}
                                    appleId={target.appleId}
                                    applePrimaryEmail={target.applePrimaryEmail}
                                    googlePrimaryEmail={
                                        target.googlePrimaryEmail
                                    }
                                    facebookId={target.facebookId}
                                    facebookPrimaryEmail={
                                        target.facebookPrimaryEmail
                                    }
                                    microsoftId={target.microsoftId}
                                    microsoftPrimaryEmail={
                                        target.microsoftPrimaryEmail
                                    }
                                    isClasslinkUser={target.isClasslinkUser}
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {/* twoFactorAuthFlag gates two-factor auth settings
                        to be for internal users only. */}
                        {twoFactorAuthFlag &&
                            !requestInfo.isModifyingChild &&
                            !target.isChild &&
                            target.hasSendableEmail &&
                            target.authEmails.length > 0 &&
                            target.isEmailVerified && (
                                <React.Fragment>
                                    <SecuritySettings
                                        kaid={target.id}
                                        email={target.authEmails[0]}
                                        isTotpTwoFactorEnabled={
                                            target.isTotpTwoFactorEnabled
                                        }
                                        onSettingsChanged={
                                            handleSuccessfulChange
                                        }
                                    />
                                    <View style={styles.divider} />
                                </React.Fragment>
                            )}
                        <AccessibilitySettings
                            initialSettings={{
                                hideVisual,
                                prefersReducedMotion,
                                noColorInVideos,
                                soundOn,
                            }}
                            kaid={target.id}
                            successfullyChangedSettings={handleSuccessfulChange}
                        />
                        <View style={styles.divider} />
                        {!target.isChild && (
                            <React.Fragment>
                                <RolesSection
                                    canAccessDistrictsHomepage={
                                        !!target.canAccessDistrictsHomepage
                                    }
                                    isEligibleToBeParent={
                                        target.isEligibleToBeParent
                                    }
                                    userChosenRoles={
                                        target.userChosenRoles || []
                                    }
                                    homepage={target.homepage}
                                    successfullyChangedSettings={
                                        handleSuccessfulChange
                                    }
                                />
                                <View style={styles.divider} />
                                {newGradeLevelsAndSubjectsSection &&
                                    target.includesDistrictOwnedData && (
                                        <>
                                            <GradeLevelsAndSubjectsSection
                                                kaid={target.id}
                                            />
                                            <View style={styles.divider} />
                                        </>
                                    )}
                            </React.Fragment>
                        )}
                        {target.canSetTeacherAffiliation && (
                            <React.Fragment>
                                <SchoolSection
                                    schoolAffiliation={target.schoolAffiliation}
                                    affiliationCountryCode={
                                        target.affiliationCountryCode
                                    }
                                />
                                <View style={styles.divider} />

                                {newGradeLevelsAndSubjectsSection &&
                                    target.userChosenRoles?.includes(
                                        "teacher",
                                    ) &&
                                    !target.includesDistrictOwnedData && (
                                        <>
                                            <GradeLevelsAndSubjectsSection
                                                kaid={target.id}
                                            />
                                            <View style={styles.divider} />
                                        </>
                                    )}

                                <TeacherAffiliationSection
                                    kaid={target.id}
                                    hasFormalTeacherStatus={
                                        target.tosForFormalTeacherStatus
                                    }
                                    includesDistrictOwnedData={
                                        target.includesDistrictOwnedData
                                    }
                                    previouslyOptedOutOfReasearch={
                                        target.hasUserOptedOutFromABTesting
                                    }
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {target.hasChildren && (
                            <React.Fragment>
                                <ChildrenSection
                                    managedChildren={
                                        target.maybeManagedChildren
                                    }
                                    unmanagedChildren={target.unmanagedChildren}
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {!target.isChild && (
                            <React.Fragment>
                                <PublicProfileInfoSection
                                    successfullyChangedSettings={
                                        handleSuccessfulChange
                                    }
                                />
                                <View style={styles.divider} />
                            </React.Fragment>
                        )}
                        {!target.includesCentrallyOwnedDistrictData && (
                            <React.Fragment>
                                <DeleteAccount
                                    actorIdentifier={actor.identifier}
                                    isClasslinkUser={target.isClasslinkUser}
                                    actorIsChild={actor.isChild}
                                    isModifyingChild={
                                        requestInfo.isModifyingChild
                                    }
                                    badgeCount={target.badgeCount}
                                    points={target.points}
                                    targetHasChildren={target.hasChildren}
                                    targetIsChild={target.isChild}
                                    targetNickname={
                                        target.settings.nickname ||
                                        target.settings.username ||
                                        ""
                                    }
                                    targetId={target.id}
                                />
                                <Strut size={spacing.xxLarge_48} />
                            </React.Fragment>
                        )}
                    </View>
                );
            }}
        </AccountSettingsQuery>
    );
};

const styles = StyleSheet.create({
    divider: {
        height: 1,
        backgroundColor: color.offBlack16,
        marginTop: spacing.xxLarge_48,
        marginBottom: spacing.xxLarge_48,
    },
    spinner: {
        alignItems: "center",
    },
    text: {
        color: color.offBlack,
    },
});

export default AccountSettings;
